import { getResourceIconClass } from "@/modules/api/resourceTypes.tsx";
import type { EBillingAccount } from "@/modules/billing/billingLogic.tsx";
import {
    AddMethodResult,
    type AddMethodActions,
    type AddMethodParams,
    type MethodAdderParams,
} from "@/modules/billing/choose_method/AddMethodParams.ts";
import type { ChooseMethodContentProps, ChooseMethodProps } from "@/modules/billing/choose_method/ChooseMethod.tsx";
import { invalidateBillingCards } from "@/modules/billing/choose_method/addMethodUtils.ts";
import { getPaymentReturnUrl } from "@/modules/billing/payment/urls.ts";
import type { PaymentMethod } from "@/modules/billing/paymentMethods.ts";
import { addableMethodsAtom } from "@/modules/billing/paymentProcessorsLogic.tsx";
import { PaymentPopup } from "@/modules/billing/popup/popup.ts";
import { showWarn } from "@/modules/error/errorStream.ts";
import { getEnglishText, getTransText, type Translatable } from "@/modules/l10n/getTransText.ts";
import { raiseBackgroundErrorToast } from "@/modules/notifications/toast.tsx";
import { userDataAtom } from "@/modules/user/userApi.ts";
import { unhandledNestedError } from "@/utils/error.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { useOnce } from "@/utils/react/useOnce.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import { useQueryClient } from "@tanstack/react-query";
import { notNull } from "@warrenio/utils/notNull";
import { useAtomValue, useStore } from "jotai/react";
import { atom } from "jotai/vanilla";
import { useEffect, useMemo, useRef, useState } from "react";
import type { PressEvent } from "react-aria";
import invariant from "tiny-invariant";

export interface UseAddMethodProps {
    defaultValue?: ChooseMethodProps["value"];
    account: EBillingAccount | undefined;
}

export function useAddMethod(props: UseAddMethodProps) {
    const methods = useAtomValue(addableMethodsAtom);
    return useAddMethodWithProcessors(methods, props);
}

export function raisePaymentAddError(err: unknown) {
    raiseBackgroundErrorToast(err, <Trans>Adding payment method failed</Trans>, {
        type: "error",
        icon: getResourceIconClass("billing_account"),
    });
}

export const t_PopupFailError = <Trans>Please allow popup windows to use this payment method</Trans>;

export function useAddMethodWithProcessors(
    availableMethods: PaymentMethod[],
    { account, defaultValue }: UseAddMethodProps,
) {
    const queryClient = useQueryClient();
    const user = useSuspenseQueryAtom(userDataAtom);
    const methods = useMemo(
        () => availableMethods.filter((m) => m.canAddToAccount?.({ account, user }) ?? true),
        [availableMethods, account, user],
    );

    const [value, setValue] = useState<ChooseMethodProps["value"]>(() => {
        if (defaultValue == null) {
            return null;
        }
        if (!methods.some((m) => m.id === defaultValue)) {
            showWarn(
                "Invalid default value for `useAddMethod`: %o (valid: %o)",
                defaultValue,
                methods.map((m) => m.id),
            );
            return null;
        }
        return defaultValue;
    });
    const progress = usePopupProgress("Add method");

    // Forward actions to the form via a ref / `useImperativeHandle`
    const actionsRef = useRef<AddMethodActions>(null);
    function getActions() {
        return notNull(actionsRef.current, "form ref");
    }

    /** `null` means no popup needed */
    const popupRef = useRef<PaymentPopup | null>(undefined);

    function dispose() {
        popupRef.current?.dispose();
    }

    useEffect(() => {
        return () => {
            dispose();
        };
    }, []);

    return {
        async validate(ev: PressEvent | null) {
            if (value == null) {
                console.warn("No payment method selected");
                return false;
            }

            if (!(await getActions().validate())) {
                return false;
            }

            // Open the popup in the early validation function because `addPaymentMethod` might be called asynchronously later
            if (getActions().needsPopUp?.()) {
                // NB: The popup must be opened synchronously (before anything `async`), otherwise the browser (eg. Safari) will block it
                const popup = PaymentPopup.open(ev?.pointerType !== "virtual");
                if (!popup) {
                    raisePaymentAddError(t_PopupFailError);
                    return false;
                }

                // Close the previous popup, if it exists (in case of a retry)
                dispose();

                popupRef.current = popup;
            } else {
                popupRef.current = null;
            }
            return true;
        },

        async addPaymentMethod(params: MethodAdderParams): Promise<AddMethodResult> {
            invariant(popupRef.current !== undefined, "Popup must be prepared before calling (by `validate()`)");

            const isValid = await getActions().validate();
            invariant(isValid, "Must be validate()'d before calling");

            const addParams: AddMethodParams = {
                // TODO: Use a specific return URL for adding payment methods
                returnUrl: getPaymentReturnUrl(),

                progress: (s) => progress.update(popupRef.current, s),
                popup: popupRef.current,
                ...params,
            };

            try {
                return (await getActions().addPaymentMethod(addParams)) ?? AddMethodResult.SUCCESS;
            } finally {
                dispose();

                try {
                    await invalidateBillingCards(queryClient, params.account.id);
                } catch (e) {
                    unhandledNestedError(e);
                }
            }
        },

        /** NB: MUST be called after `validate()` returns `true` regardless of whether `addPaymentMethod()` is called or not */
        dispose,

        /** Props to send to <ChooseMethod> */
        props: {
            formProps: { actionsRef, account },
            value,
            onChange: setValue,
            methods,
            statusAtom: progress.statusAtom,
            // TODO: Set `isDisabled` when action is in progress
        } satisfies ChooseMethodContentProps,

        hasSelectedMethod: value != null,
    };
}

/** Utility hook to manage popup progress display. */
export function usePopupProgress(debugTitle: string) {
    const { i18n } = useLingui();
    const statusAtom = useOnce(() => atom<string | null>(null));
    const store = useStore();

    return {
        statusAtom,
        update(popup: PaymentPopup | null | undefined, statusT: Translatable | null) {
            console.info("%s progress: %s", debugTitle, statusT != null ? getEnglishText(statusT) : "null");
            const status = statusT != null ? `${getTransText(statusT, i18n)}…` : null;
            store.set(statusAtom, status);
            if (popup) {
                // TODO: Check if window closed
                popup.setText(status ?? "");
            }
        },
        clear() {
            this.update(undefined, null);
        },
    };
}
